// Plant membership chips + add picker for Admin → Active Members.
import { adminInputClass } from "@/components/common/surfaceClasses";
import type { MemberWithProfile } from "./adminTypes";

export type AdminPlantOption = {
  id: string;
  plant_code: string;
  name: string;
};

export function plantMembershipLabel(
  plantId: string,
  plantOptions: readonly AdminPlantOption[],
): string {
  const p = plantOptions.find((x) => x.id === plantId);
  return p ? `${p.plant_code} · ${p.name}` : plantId.slice(0, 8);
}

export function AdminMemberPlantChips({
  member,
  isSelf,
  isCollapsed,
  loading,
  userPlantIds,
  plantOptions,
  onRemoveUserPlant,
}: {
  member: MemberWithProfile;
  isSelf: boolean;
  isCollapsed: boolean;
  loading: boolean;
  userPlantIds: string[];
  plantOptions: AdminPlantOption[];
  onRemoveUserPlant: (member: MemberWithProfile, plantId: string) => void;
}) {
  if (userPlantIds.length === 0) return null;
  return (
    <>
      {userPlantIds.map((plantId) => (
        <span
          key={plantId}
          className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-2 py-0.5 dark:bg-teal-950/40 dark:text-teal-200 dark:border-teal-800"
        >
          {plantMembershipLabel(plantId, plantOptions)}
          {!isCollapsed && !isSelf ? (
            <button
              type="button"
              onClick={() => void onRemoveUserPlant(member, plantId)}
              disabled={loading}
              className="text-teal-400 hover:text-red-500 leading-none ml-0.5"
              title={`Remove plant ${plantMembershipLabel(plantId, plantOptions)}`}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
    </>
  );
}

export function AdminMemberPlantAddPicker({
  member,
  loading,
  userPlantIds,
  plantOptions,
  addPlantByUser,
  setAddPlantByUser,
  onAddUserPlant,
}: {
  member: MemberWithProfile;
  loading: boolean;
  userPlantIds: string[];
  plantOptions: AdminPlantOption[];
  addPlantByUser: Record<string, string>;
  setAddPlantByUser: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onAddUserPlant: (member: MemberWithProfile, plantId: string) => void;
}) {
  if (plantOptions.length === 0) return null;
  return (
    <div className="inline-flex items-center gap-1">
      <select
        className={`text-xs rounded-lg px-1.5 py-0.5 ${adminInputClass}`}
        value={addPlantByUser[member.user_id] ?? ""}
        onChange={(e) =>
          setAddPlantByUser((prev) => ({ ...prev, [member.user_id]: e.target.value }))
        }
        disabled={loading}
      >
        <option value="">+ add plant</option>
        {plantOptions
          .filter((p) => !userPlantIds.includes(p.id))
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.plant_code} · {p.name}
            </option>
          ))}
      </select>
      {addPlantByUser[member.user_id] ? (
        <button
          type="button"
          onClick={() => void onAddUserPlant(member, addPlantByUser[member.user_id])}
          disabled={loading}
          className="text-xs px-2 py-0.5 rounded-lg bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40"
        >
          Add
        </button>
      ) : null}
    </div>
  );
}
